import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { DashboardLayout } from "../../layouts/DashboardLayout"
import { LineChart, HBarChart, DonutChart, ChartCard } from "../analytics/Charts"
import {
  getAdminOverviewApi, AdminOverview,
  getAdminUsersAnalyticsApi, AdminUsersAnalytics,
  getAdminCoursesApi, AdminCourseListItem,
} from "../../features/analytics/api/adminAnalytics.api"

import {
  UsersIcon,
  AcademicCapIcon,
  BriefcaseIcon,
  BuildingOffice2Icon,
  ArrowRightIcon,
  ArrowTrendingUpIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/16/solid"

export const DashboardPage = () => {
  const navigate = useNavigate()

  const [overview, setOverview] = useState<AdminOverview | null>(null)
  const [users, setUsers] = useState<AdminUsersAnalytics | null>(null)
  const [courses, setCourses] = useState<AdminCourseListItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  const load = () => {
    setLoading(true)
    setError(false)
    Promise.all([
      getAdminOverviewApi(),
      getAdminUsersAnalyticsApi(),
      getAdminCoursesApi({ sort_by: "purchases", limit: 5 }),
    ])
      .then(([o, u, c]) => {
        setOverview(o.data)
        setUsers(u.data)
        setCourses(c.data.items)
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false))
  }

  useEffect(() => { load() }, [])

  if (error) {
    return (
      <DashboardLayout title="Дашборд">
        <div className="bg-white border border-gray-100 rounded-2xl py-16 text-center">
          <ExclamationTriangleIcon className="w-8 h-8 text-red-200 mx-auto mb-2" />
          <p className="text-sm text-gray-400 mb-2">Не удалось загрузить дашборд</p>
          <button onClick={load} className="text-xs font-medium text-blue-600 hover:text-blue-700 underline">
            Повторить
          </button>
        </div>
      </DashboardLayout>
    )
  }

  if (loading || !overview || !users) {
    return (
      <DashboardLayout title="Дашборд">
        <div className="space-y-6">
          <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => <div key={i} className="bg-white rounded-2xl border border-gray-100 h-28 animate-pulse" />)}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {[1, 2].map((i) => <div key={i} className="bg-white rounded-2xl border border-gray-100 h-52 animate-pulse" />)}
          </div>
        </div>
      </DashboardLayout>
    )
  }

  const top5courses = [...courses].sort((a, b) => b.purchases - a.purchases).slice(0, 5)

  const STAT_CARDS = [
    {
      label: "Пользователей",
      value: overview.users.total,
      sub: `+${overview.users.new_last_30_days} за 30 дней`,
      icon: <UsersIcon className="w-4 h-4 text-blue-600" />,
      bg: "bg-blue-50",
      iconBg: "bg-blue-100",
      color: "text-blue-600",
      href: "/admin/analytics/users",
    },
    {
      label: "Покупок курсов",
      value: overview.courses.total_purchases,
      sub: `Конверсия ${overview.courses.avg_conversion_rate}%`,
      icon: <AcademicCapIcon className="w-4 h-4 text-violet-600" />,
      bg: "bg-violet-50",
      iconBg: "bg-violet-100",
      color: "text-violet-600",
      href: "/admin/analytics/courses",
    },
    {
      label: "Заявок на вакансии",
      value: overview.vacancies.total_applications,
      sub: `${overview.vacancies.total} активных вакансий`,
      icon: <BriefcaseIcon className="w-4 h-4 text-emerald-600" />,
      bg: "bg-emerald-50",
      iconBg: "bg-emerald-100",
      color: "text-emerald-600",
      href: "/admin/analytics/vacancies",
    },
    {
      label: "Компаний Китая",
      value: overview.china_enterprises.total,
      sub: `${overview.china_enterprises.total_views.toLocaleString("ru")} просмотров`,
      icon: <BuildingOffice2Icon className="w-4 h-4 text-red-600" />,
      bg: "bg-red-50",
      iconBg: "bg-red-100",
      color: "text-red-600",
      href: "/admin/analytics/china",
    },
  ]

  return (
    <DashboardLayout title="Дашборд">
      <div className="space-y-6">

        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Обзор платформы</h2>
            <p className="text-sm text-gray-400 mt-0.5">Актуальные метрики</p>
          </div>
          <button
            onClick={() => navigate("/admin/analytics/overview")}
            className="flex items-center gap-1.5 text-sm font-medium text-blue-600 hover:text-blue-700 bg-blue-50 hover:bg-blue-100 px-3.5 py-2 rounded-xl transition-colors"
          >
            <ArrowTrendingUpIcon className="w-4 h-4" />
            Детальная аналитика
          </button>
        </div>

        {/* Stat cards */}
        <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
          {STAT_CARDS.map((s) => (
            <button key={s.label} onClick={() => navigate(s.href)}
              className={`${s.bg} rounded-2xl p-5 text-left hover:shadow-sm hover:scale-[1.01] transition-all group`}>
              <div className="flex items-center justify-between mb-3">
                <span className={`${s.iconBg} w-9 h-9 rounded-xl flex items-center justify-center`}>
                  {s.icon}
                </span>
                <ArrowRightIcon className="w-3.5 h-3.5 text-gray-300 group-hover:text-gray-500 transition-colors" />
              </div>
              <p className={`text-2xl font-bold ${s.color}`}>{s.value.toLocaleString("ru")}</p>
              <p className="text-xs font-medium text-gray-700 mt-0.5">{s.label}</p>
              <p className="text-[11px] text-gray-400 mt-1">{s.sub}</p>
            </button>
          ))}
        </div>

        {/* Charts row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <ChartCard title="Новые регистрации (30 дней)">
            <LineChart
              data={users.new_by_day.map((r) => ({ label: r.date, value: r.count }))}
              color="#3b82f6"
            />
          </ChartCard>

          <ChartCard title="Топ-5 курсов по покупкам">
            <HBarChart
              data={top5courses.map((c) => ({ label: c.title.slice(0, 20), value: c.purchases }))}
              color="#8b5cf6"
            />
          </ChartCard>
        </div>

        {/* Bottom row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* Roles donut */}
          <ChartCard title="Роли пользователей">
            <DonutChart data={[
              { label: "Пользователи",    value: users.by_role?.user ?? 0,    color: "#3b82f6" },
              { label: "Менторы",         value: users.by_role?.mentor ?? 0,  color: "#8b5cf6" },
              { label: "Администраторы",  value: users.by_role?.admin ?? 0,   color: "#f59e0b" },
            ]} />
          </ChartCard>

          {/* Quick links */}
          <div className="lg:col-span-2 bg-white border border-gray-100 rounded-2xl p-5">
            <h3 className="text-sm font-semibold text-gray-700 mb-4">Быстрые переходы</h3>
            <div className="grid grid-cols-2 gap-2">
              {[
                { label: "Аналитика пользователей", href: "/admin/analytics/users",    color: "hover:bg-blue-50 hover:text-blue-700" },
                { label: "Аналитика курсов",        href: "/admin/analytics/courses",  color: "hover:bg-violet-50 hover:text-violet-700" },
                { label: "Аналитика менторов",      href: "/admin/analytics/mentors",  color: "hover:bg-indigo-50 hover:text-indigo-700" },
                { label: "Аналитика вакансий",      href: "/admin/analytics/vacancies",color: "hover:bg-emerald-50 hover:text-emerald-700" },
                { label: "Компании Китая",          href: "/admin/analytics/china",    color: "hover:bg-red-50 hover:text-red-700" },
                { label: "Все курсы",               href: "/courses",                  color: "hover:bg-gray-50 hover:text-gray-700" },
                { label: "Пользователи",            href: "/users",                    color: "hover:bg-gray-50 hover:text-gray-700" },
                { label: "Вакансии",               href: "/vacancies",                color: "hover:bg-gray-50 hover:text-gray-700" },
              ].map(({ label, href, color }) => (
                <button key={href} onClick={() => navigate(href)}
                  className={`flex items-center justify-between px-4 py-3 rounded-xl border border-gray-100 text-sm text-gray-500 transition-colors ${color}`}>
                  {label}
                  <ArrowRightIcon className="w-3.5 h-3.5 opacity-40" />
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  )
}
